import { useDispatch } from 'react-redux';
import { MenuType } from './dto/module/user.type';
import { AuthUserState } from './dto/response';
import { useLocation } from 'react-router-dom';
import { setPermission } from '../redux/reducer/permissionSlice';
import store from '../redux/store';

const CryptoJS = require('crypto-js');

export function encryptText(plainText: any) {
    if (!plainText) return ''
    let key = CryptoJS.enc.Utf8.parse('22fd580b89e9c1db824253b34d8476fec9ec29d4dce249b980d0e7d34dce5b0c');
    let iv = CryptoJS.enc.Utf8.parse('9c2b9115327c834e3fa023377ad3f999');
    let config = { keySize: 128, iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 };
    let encPassword = CryptoJS.AES.encrypt(plainText, key, config);
    return encPassword.ciphertext.toString(CryptoJS.enc.Base64);
}

export const decryptText = (text: any) => {
    if (!text) return ''
    let key = CryptoJS.enc.Utf8.parse('22fd580b89e9c1db824253b34d8476fec9ec29d4dce249b980d0e7d34dce5b0c');
    let iv = CryptoJS.enc.Utf8.parse('9c2b9115327c834e3fa023377ad3f999');
    let config = { keySize: 128, iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 };
    try {
        let encPassword = CryptoJS.AES.decrypt(text, key, config);
        return encPassword.toString(CryptoJS.enc.Utf8);
    } catch (err) {
        try {
            return JSON.parse(text)
        } catch (err1) {
            return text || ''
        }
    }
}

export const isObject = (obj: any) => typeof obj === 'object' && !Array.isArray(obj) && obj !== null

export const stableSort = <T>(array: readonly T[], comparator: (a: T, b: T) => number) => {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export const getComparator = <Key extends keyof any>(order: any, orderBy: Key,): (
    a: { [key in Key]: any },
    b: { [key in Key]: any },
) => number => {
    return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

export const descendingComparator = <T>(a: T, b: T, orderBy: keyof T) => {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

export const isLoggedIn = (state: AuthUserState) => {
    const { Token, User, ExpiresAt } = state;
    const currentTime = Date.now();
    
    return !!User && !!Token ;
}

export const getAllModuleUrls = (dispatch: any): string[] => {
    let moduleUrls: string[] = store.getState().permission?.moduleUrls ?? [];
    if (!moduleUrls || moduleUrls.length === 0) {
        try {
            const rolesPermissions = localStorage.getItem('roles_permissions');
            const mainPermissionsObject = rolesPermissions ? JSON.parse(decryptText(rolesPermissions)) : {};
            
            const collectUrls = (menus: any[]): string[] =>
                menus.flatMap((item: { url: string; sub_menus?: any[] }) => [
                    item.url,
                    ...(item.sub_menus ? collectUrls(item.sub_menus) : []),
                ]).filter((url: string) => url?.trim() !== "");
            moduleUrls = mainPermissionsObject.menus
                ? collectUrls(mainPermissionsObject.menus)
                : [];

            const permissionState = store.getState().permission ?? {};
            dispatch(setPermission({ ...permissionState, moduleUrls }));
        } catch (error) {
            console.error("Error fetching module URLs from local storage:", error);
            moduleUrls = [];
        }
    }
    return moduleUrls;
};

export const isAuthorizedPage = (dispatch: any, location: any): boolean => {
    const currentPath = location.pathname.split('?')[0]; 

    const moduleUrls = getAllModuleUrls(dispatch);

    if (moduleUrls.includes(currentPath)) {
        return true;
    }

    try {
        const rolesPermissions = JSON.parse(decryptText(localStorage.getItem('roles_permissions')) || '{}');
        const globalMenu = rolesPermissions.menus?.find((menu: any) => menu.slug === 'global');
        if (globalMenu && globalMenu.sub_menus) {
            const matchingSubMenu = globalMenu.sub_menus.find((subMenu: any) => subMenu.url === currentPath);
            if (matchingSubMenu && matchingSubMenu.permissions?.length > 0) {
                return matchingSubMenu.permissions.some((perm: string) => 
                    globalMenu.permissions.includes(perm) || matchingSubMenu.permissions.includes(perm)
                );
            }
        }
    } catch (error) {
        console.error("Error checking global permissions:", error);
    }

    return false;};

export const hasAccessToPath = (menus: any, path: string): MenuType | null => {
    if (!menus) return null;

    for (const menu of menus) {
        if (menu.url === path) {
            return menu;
        }

        if (menu.sub_menus && menu.sub_menus.length > 0 && hasAccessToPath(menu.sub_menus, path)) {
            return menu.sub_menus.find((menu: any) => menu.url === path) || null;
        }
    }

    return null;
};

export const dialogSizeMaxHeightMap: Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', string> = {
    xs: '30rem',
    sm: '35rem',
    md: '40rem',
    lg: '48rem',
    xl: '60rem',
};
  